"use client";

/**
 * Worked example revealed one step at a time — the student predicts the next
 * move, then reveals it to check their thinking. Retrieval practice with no
 * grading, so the full step list in the payload is not a secret.
 *
 * Step text supports inline math via $...$ (KaTeX renders it; the stylesheet
 * is loaded globally in the root layout).
 */

import katex from "katex";
import { Check, Footprints, RotateCcw } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import AuthImage from "../AuthImage";
import ActivityFrame from "./ActivityFrame";

export interface RevealStep {
  text: string;
  asset_id?: string;
  alt?: string;
}

// Accepts the legacy string form and the {text, asset_id?, alt?} object form
type StepInput = string | RevealStep;
function normalizeStep(step: StepInput): RevealStep {
  return typeof step === "string" ? { text: step } : step;
}

/** Render a plain string with $...$ inline-math segments. Fails soft: a
 * segment KaTeX can't parse renders as its literal text. */
export function MathText({ text }: { text: string }) {
  const parts = text.split(/\$([^$]+)\$/g);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;
        let html: string;
        try {
          html = katex.renderToString(part, { throwOnError: true });
        } catch {
          return <Fragment key={i}>${part}$</Fragment>;
        }
        // KaTeX output from instructor-authored math — safe HTML by construction
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </>
  );
}

export default function StepRevealActivity({
  courseId,
  title,
  prompt,
  steps,
  onComplete,
}: {
  // Needed only to render step images (auth-scoped assets)
  courseId?: string;
  title: string;
  prompt?: string;
  steps: StepInput[];
  // Called once when every step has been revealed
  onComplete?: () => void;
}) {
  const [revealed, setRevealed] = useState(1);
  const completed = useRef(false);

  if (steps.length === 0) return null;
  const stepList = steps.map(normalizeStep);
  const done = revealed >= steps.length;

  const revealNext = () => {
    const next = Math.min(revealed + 1, steps.length);
    setRevealed(next);
    if (next >= steps.length && !completed.current) {
      completed.current = true;
      onComplete?.();
    }
  };

  return (
    <ActivityFrame
      type="step_reveal"
      kindLabel="Worked example"
      icon={Footprints}
      title={title}
      meta={
        done ? (
          <span className="activity__done" role="status">
            <Check className="h-3.5 w-3.5" /> All steps shown
          </span>
        ) : (
          `${revealed} / ${steps.length}`
        )
      }
    >
      {prompt && (
        <p className="mb-3 rounded-md border border-[var(--la-line)] bg-[var(--la-soft)]/60 px-3 py-2 text-sm">
          <MathText text={prompt} />
        </p>
      )}

      <ol className="space-y-2" aria-live="polite">
        {stepList.slice(0, revealed).map((step, i) => (
          <li key={i} className="seq-step !items-start">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--la-soft)] text-[11px] font-bold text-[var(--la-ink)]">
              {i + 1}
            </span>
            <span className="flex-1">
              <MathText text={step.text} />
              {step.asset_id && courseId && (
                <AuthImage
                  courseId={courseId}
                  assetId={step.asset_id}
                  alt={step.alt ?? ""}
                  className="mt-2 max-h-64 rounded-lg border border-line"
                />
              )}
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-3 flex items-center justify-between">
        {done ? (
          <button
            onClick={() => setRevealed(1)}
            className="activity__btn flex items-center gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Start over
          </button>
        ) : (
          <span className="text-xs text-faint">
            Think about what comes next before revealing it.
          </span>
        )}
        {!done && (
          <button
            onClick={revealNext}
            className="rounded-md px-4 py-1.5 text-sm font-medium text-white transition-opacity"
            style={{ background: "var(--la)" }}
          >
            Show the next step
          </button>
        )}
      </div>
    </ActivityFrame>
  );
}
